import type { Module } from 'vuex'
import type { RootState } from '@/store/modules/types'

export type DrawerState = {
  drawerTitle?: string
  drawerIcon?: string
  drawerSubtitle?: string
  isDrawerOpen: boolean
  drawerComponentName?: string
  drawerComponent?: any
  drawerComponentProps: {}
  hasHeaderSlot: boolean
}

export const initialDrawerState = (): DrawerState => ({
  drawerTitle: '', // displayed at the top, beside the X close icon
  drawerIcon: '', // icon displayed next to the title
  drawerSubtitle: '', // displayed below the title (e.g., status badge, type indicator)
  isDrawerOpen: false, // shows / hides
  drawerComponentName: '', // Component name for display
  drawerComponent: undefined, // Actual component instance
  drawerComponentProps: {}, // Properties to add to component
  hasHeaderSlot: false, // Whether the component uses a custom header slot
})

const globalDrawerModule: Module<DrawerState, RootState> = {
  namespaced: true,
  state: initialDrawerState,
  mutations: {

    SET_IS_DRAWER_OPEN(state: DrawerState, payload: boolean) {
      state.isDrawerOpen = payload
    },

    /**
         * Set the title of the drawer.  This is usually done in the same function that defines the component.
         * @param state - The state of the application module.
         * @param payload - The title of the drawer.
         */
    SET_DRAWER_TITLE(state: DrawerState, payload: string) {
      state.drawerTitle = payload
    },

    /**
     * Set the icon of the drawer. This icon is displayed next to the title.
     * @param state - The state of the application module.
     * @param payload - The icon type/name for the drawer.
     */
    SET_DRAWER_ICON(state: DrawerState, payload: string) {
      state.drawerIcon = payload
    },

    /**
     * Set the subtitle of the drawer. This is displayed below the title (e.g., status badge).
     * @param state - The state of the application module.
     * @param payload - The subtitle text for the drawer.
     */
    SET_DRAWER_SUBTITLE(state: DrawerState, payload: string) {
      state.drawerSubtitle = payload
    },

    /**
         * Define a component that's imported into the drawer and displayed.  It's important to note that the component must be
            * registered before it's added to the store, we are using the actual SFC rather than the component name.
        */

    SET_DRAWER_COMPONENT_NAME(state: DrawerState, payload: string) {
      state.drawerComponentName = payload
    },

    SET_DRAWER_COMPONENT(state: DrawerState, payload: any) {
      state.drawerComponent = payload
    },

    /**
         * Set the vue props of the drawer component.
         * @param state - The state of the application module.
         * @param payload - The props to be set for the drawer component.
         */

    SET_DRAWER_COMPONENT_PROPS(state: DrawerState, payload: {}) {
      state.drawerComponentProps = payload
    },

    /**
     * Set whether the drawer should use a custom header slot
     * @param state - The state of the application module.
     * @param payload - Boolean indicating if custom header should be used
     */
    SET_HAS_HEADER_SLOT(state: DrawerState, payload: boolean) {
      state.hasHeaderSlot = payload
    },

  }, getters: {
    isDrawerOpen: (state: DrawerState) => state.isDrawerOpen, // show / hide the drawer
    drawerTitle: (state: DrawerState) => state.drawerTitle, // the title of the drawer, displayed at the top
    drawerIcon: (state: DrawerState) => state.drawerIcon, // the icon of the drawer, displayed next to the title
    drawerSubtitle: (state: DrawerState) => state.drawerSubtitle, // the subtitle of the drawer, displayed below the title
    drawerComponentName: (state: DrawerState) => state.drawerComponentName, // the component's name.  For example "globalDrawerFilter"
    drawerComponent: (state: DrawerState) => state.drawerComponent, // the actual component instance
    drawerComponentProps: (state: DrawerState) => state.drawerComponentProps, // the inheritable props for the component.
    hasHeaderSlot: (state: DrawerState) => state.hasHeaderSlot, // whether the component uses a custom header slot
  },
}

export default globalDrawerModule
